import './Layout.scss';

import { ButtonProps, Loader, MenuItemProps, TopNavigation, TopNavigationProps } from '@trackman/web-shared-components';
import { Can, Cannot } from 'components/Can';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { AppPermissions } from 'constants/AppPermissions';
import AppSwitcher from 'components/AppSwitcher/AppSwitcher';
import { ApplicationUserRights } from 'stores/ApplicationUserRightsStore';
import { DefaultRoutes } from 'Routes';
import Forbidden from 'pages/Forbidden/Forbidden';
import LoggedOutInfoModal from 'components/LoggedOutInfoModal/LoggedOutInfoModal';
import { Notifications } from 'components/Notifications';
import ProfileIconMenu from 'components/ProfileIconMenu/ProfileIconMenu';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';

type Props = {
  children: React.ReactNode;
};

type TopLevelMenuLabel = 'Front Page' | 'Test Submenu';

type Navigation = Array<
  {
    label: TopLevelMenuLabel;
  } & (
    | ({
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
        href: string;
        need: string[];
        submenu?: undefined;
      } & ButtonProps)
    | {
        onClick?: undefined;
        href?: undefined;
        need?: undefined;
        submenu: ({
          label: string;
          onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
          href: string;
          need: string[];
        } & MenuItemProps)[];
      }
  )
>;

type ValueOf<T> = T[keyof T];
type DefaultRoute = ValueOf<typeof DefaultRoutes>;

const routeToActiveLabel: Record<DefaultRoute, TopLevelMenuLabel> = {
  '/Homepage': 'Front Page',
};

const Layout = observer(({ children }: Props) => {
  const ss = useStores().startupStore;
  const history = useHistory();
  const location = useLocation();

  const [displayContent, setDisplayContent] = useState(false);

  useEffect(() => {
    if (ss.hasBearerToken) setDisplayContent(true);
  }, [ss.hasBearerToken]);

  const menuSrtucture: Navigation = useMemo(
    () => [
      // Example of a menu item without a submenu
      {
        label: 'Front Page',
        onClick: (e) => {
          e.preventDefault(); // Needed to prevent the default behavior of <a> tag with href
          history.push(DefaultRoutes.FrontPage);
        },
        href: DefaultRoutes.FrontPage, // Needed to support opening in new tab
        need: [], // Allows to define permissions for the menu item and show / hide it in UI
      },
    ],
    [history]
  );

  const activeNavigationLabel = useMemo(() => {
    const route = '/' + location.pathname.split('/').filter((x) => x !== '')[0];
    return routeToActiveLabel[route];
  }, [location.pathname]);

  const navigation: TopNavigationProps['navigation'] = useMemo(() => {
    if (!ss.hasBearerToken) return [];

    const menuSrtuctureTemp = [...menuSrtucture];

    menuSrtuctureTemp.forEach((item, index, object) => {
      if (item.need && !ApplicationUserRights.IsAllowed(item.need)) object.splice(index, 1);
      if (item.submenu) {
        item.submenu.forEach((subItem, index, object) => {
          if (!ApplicationUserRights.IsAllowed(subItem.need)) object.splice(index, 1);
        });
        if (item.submenu.length === 0) object.splice(index, 1);
      }
    });
    return menuSrtuctureTemp;
  }, [ss.hasBearerToken, menuSrtucture]);

  return (
    <div className='layout-content-container'>
      <div className='top-navigation'>
        <TopNavigation
          appName={'Tagging App Dispatcher'}
          navigation={navigation}
          activeNavigationLabel={activeNavigationLabel}
          utilities={
            ss.hasBearerToken && !ss.isInitializing
              ? [
                  { key: 'profile-icon-menu', component: <ProfileIconMenu setDisplayContent={setDisplayContent} /> },
                  {
                    key: 'app-switcher-icon-menu',
                    component: <AppSwitcher setDisplayContent={setDisplayContent} />,
                  },
                ]
              : []
          }
          animated
          displayContent={displayContent}
          onLogoClick={() => history.push(DefaultRoutes.FrontPage)}
        />
      </div>
      {!displayContent && (
        <div className='spinner'>
          <Loader size='large' />
        </div>
      )}
      <div
        style={{
          opacity: displayContent ? 1 : 0,
          transition: 'opacity 0.3s',
          pointerEvents: displayContent ? 'auto' : 'none',
          paddingBottom: '76px',
        }}
        className='container'
      >
        {ss.hasBearerToken && (
          <>
            <Notifications />
            <Can need={AppPermissions.PortalAccess}>{children}</Can>
            <Cannot need={AppPermissions.PortalAccess}>
              <Forbidden />
            </Cannot>
          </>
        )}
      </div>
      {ss.config && <LoggedOutInfoModal logoutUrl={ss.config.logoutUrl} />}
    </div>
  );
});

export default Layout;
